import { Button, makeStyles } from '@material-ui/core';
import PriorityHigh from '@material-ui/icons/PriorityHigh';
import { Document, Image as PDFImage, Page, pdf } from '@react-pdf/renderer';
import { ADDITIONAL_BILLING_STATUS } from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import { AdditionalBillingDocument } from 'doc-mate-store/lib/models/AdditionalBillingDocument';
import { AdditionalBillingLeg } from 'doc-mate-store/lib/models/AdditionalBillingLeg';
import { saveAs } from 'file-saver';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { PDFDocument } from 'pdf-lib';
import React, { useCallback, useMemo, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import Labeled from '../Labeled';
import ReorderableFileList from '../LoadDocument/ReorderableFileList';
import LoadDocumentPhotoList from '../LoadDocumentPhotoList';
import AdditionalBillingDropzoneSection from './AdditionalBIllingDropzoneSection';

type Props = {
  billingLeg: AdditionalBillingLeg;
};

const AdditionalBillingLoadDocument: React.FC<Props> = ({ billingLeg }) => {
  const classes = useStyles();
  const { rootStore } = useStores();
  const { me } = rootStore;
  const user = me ? me.maybeCurrent : undefined;
  const isDispatcherOrBroker = !!(user && (user.isDispatcher || user.isBroker));
  const isInvoiced = billingLeg.status === ADDITIONAL_BILLING_STATUS.INVOICED;

  const isEditable = isDispatcherOrBroker && !isInvoiced;
  const [isUploading, setIsUploading] = useState(false);

  const primaryDocument = billingLeg.getDocument('additional_documents');

  // Track & sort photo array by order property
  const photos = useMemo(() => {
    if (!primaryDocument) return [];
    return [...primaryDocument.photos]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(p => ({
        id: p.id,
        src: p.photo?.fullSize || '',
        thumbnail: p.photo?.thumbnail || '',
        width: p.width || 0,
        height: p.height || 0,
        rotate: p.rotate || 0,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    primaryDocument,
    primaryDocument?.photos,
    primaryDocument?.photos?.length,
  ]);

  // Track & sort file array by order property
  const files = useMemo(() => {
    if (!primaryDocument) return [];
    return [...primaryDocument.files].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryDocument, primaryDocument?.files, primaryDocument?.files?.length]);

  const hasNoFiles = photos.length === 0 && files.length === 0;

  const createDocument = useCallback(async () => {
    const doc = new AdditionalBillingDocument({
      id: nanoid(),
      additionalBillingLeg: billingLeg.id,
      documentType: 'additional_documents',
      createdOn: moment().format(),
      modifiedOn: moment().format(),
      comment: '',
      photos: [],
      files: [],
    });

    await doc.createDocumentWeb(rootStore.token!);
    billingLeg.addDocument?.(doc);

    return doc;
  }, [billingLeg, rootStore.token]);

  const handleUploadFiles = useCallback(
    async (uploadedFiles: File[]) => {
      if (!uploadedFiles || uploadedFiles.length === 0 || isUploading) return;

      setIsUploading(true);

      try {
        let doc = primaryDocument;
        if (!doc) {
          doc = await createDocument();
        }
        if (!doc) return;

        const photoFiles: File[] = [];
        const documentFiles: File[] = [];

        uploadedFiles.forEach(file => {
          if (file.type.startsWith('image/')) {
            photoFiles.push(file);
          } else {
            documentFiles.push(file);
          }
        });

        if (photoFiles.length > 0) {
          for (const file of photoFiles) {
            await doc.addPhotos([file] as any);
          }
        }

        if (documentFiles.length > 0) {
          for (const file of documentFiles) {
            await doc.addFiles([file] as any);
          }
        }
      } finally {
        setIsUploading(false);
      }
    },
    [createDocument, isUploading, primaryDocument],
  );

  const handleDeleteFile = useCallback(
    (fileId: number | string) => {
      if (!primaryDocument) return;

      confirmAlert({
        title: 'Confirm to Delete',
        message: 'Are you sure you want to delete this file?',
        buttons: [
          {
            label: 'Confirm',
            onClick: async () => {
              await rootStore.deleteAdditionalBillingFile(
                primaryDocument.id,
                fileId,
              );
              primaryDocument.removeFile?.(fileId);
            },
          },
          {
            label: 'Cancel',
          },
        ],
        overlayClassName: 'react-confirm-alert-overlay-custom',
      });
    },
    [primaryDocument, rootStore],
  );

  const handleDownloadPhotosPDF = useCallback(async (photoList: any[]) => {
    const PDFPhotos = () => (
      <Document>
        {photoList.map((photo: any) => (
          <Page
            key={photo.id}
            style={{ padding: 10 }}
            orientation={photo.width > photo.height ? 'landscape' : 'portrait'}
          >
            <PDFImage src={photo.src} />
          </Page>
        ))}
      </Document>
    );

    return await pdf(<PDFPhotos />).toBlob();
  }, []);

  const handleDownloadAllPdf = useCallback(async () => {
    const mergedPdf = await PDFDocument.create();
    const pdfFileName = `${billingLeg.as400ManifestNumber ||
      billingLeg.dmId}.pdf`;

    if (photos.length > 0) {
      const photosBlob = await handleDownloadPhotosPDF(photos);
      if (photosBlob) {
        const photosArrayBuffer = await photosBlob.arrayBuffer();
        const photosPdfDoc = await PDFDocument.load(photosArrayBuffer);
        const copiedPages = await mergedPdf.copyPages(
          photosPdfDoc,
          photosPdfDoc.getPageIndices(),
        );
        copiedPages.forEach(page => mergedPdf.addPage(page));
      }
    }

    for (const file of files) {
      try {
        const response = await fetch(file.file);
        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          const pdfDoc = await PDFDocument.load(arrayBuffer);
          const copiedPages = await mergedPdf.copyPages(
            pdfDoc,
            pdfDoc.getPageIndices(),
          );
          copiedPages.forEach(page => mergedPdf.addPage(page));
        }
      } catch (error) {
        console.error(`Failed to merge file ${file.name}:`, error);
      }
    }

    if (mergedPdf.getPageCount() === 0) {
      alert('No documents or photos available to export.');
      return;
    }

    const mergedPdfBytes = await mergedPdf.save();
    const finalBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
    saveAs(finalBlob, pdfFileName);
  }, [billingLeg, files, handleDownloadPhotosPDF, photos]);

  if (hasNoFiles && !isDispatcherOrBroker) {
    return (
      <div className={classes.loadDetailItem}>
        <Labeled
          label={`Additional Billing Documents (${billingLeg.billingType})`}
        >
          No documents or photos uploaded yet
        </Labeled>
      </div>
    );
  }

  return (
    <div className={classes.loadDetailItem}>
      <Labeled
        label={`Additional Billing Documents (${billingLeg.billingType})`}
      >
        <div
          style={{
            flexDirection: 'column',
            display: 'flex',
            alignItems: 'start',
          }}
        >
          {!hasNoFiles && (
            <Button onClick={handleDownloadAllPdf} style={{ marginBottom: 8 }}>
              Download All
            </Button>
          )}
        </div>

        {isDispatcherOrBroker &&
          billingLeg.status === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE && (
            <div className={classes.billingIssueNotice}>
              <PriorityHigh className={classes.priorityHigh} />
              <div>
                <span>Billing noted billing issue(s):</span>
                {billingLeg.billingIssueDescription
                  .split(/,\s*|\n/)
                  .filter(Boolean)
                  .map((issue, index) => (
                    <div key={index} className={classes.billingIssueItem}>
                      • &quot;{issue.trim()}&quot;
                    </div>
                  ))}
              </div>
            </div>
          )}

        <AdditionalBillingDropzoneSection
          isDisabled={!isEditable}
          isUploading={isUploading}
          onUpload={handleUploadFiles}
        >
          {openFileDialog => (
            <Labeled
              inner
              label="Attached Documents"
              className={classes.photoList}
            >
              {(photos.length > 0 || isDispatcherOrBroker) && (
                <div className={classes.photoListWrapper}>
                  <LoadDocumentPhotoList
                    caption={`Additional Billing (${billingLeg.billingType})`}
                    photos={photos}
                    onAdd={openFileDialog}
                    document={primaryDocument}
                    canUpdate={isEditable && !isUploading}
                  />
                </div>
              )}

              {files.length > 0 && (
                <ReorderableFileList
                  files={files.map(file => ({
                    id: file.id,
                    file: file.file,
                    name: file.name || file.file.split('/').pop() || '',
                  }))}
                  document={primaryDocument}
                  isEditable={isEditable && !isUploading}
                  onDeleteConfirm={(_doc, fileId) => handleDeleteFile(fileId)}
                />
              )}
            </Labeled>
          )}
        </AdditionalBillingDropzoneSection>
      </Labeled>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  loadDetailItem: {
    alignItems: 'center',
    display: 'flex',
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    fontWeight: 500,
  },
  photoListWrapper: {
    marginLeft: 8,
    marginTop: 4,
  },
  photoList: {
    marginBottom: theme.spacing(1),
    clear: 'both',
  },
  billingIssueNotice: {
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5),
  },
  billingIssueItem: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  priorityHigh: {
    height: 20,
  },
}));

export default observer(AdditionalBillingLoadDocument);
